import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';

interface DeviceState {
  isOnline: boolean;
  batteryLevel: number | null;
  isCharging: boolean;
}

export function useDeviceState() {
  const [state, setState] = useState<DeviceState>({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    batteryLevel: null,
    isCharging: false
  });

  useEffect(() => {
    let isMounted = true;
    const isNative = Capacitor.isNativePlatform();

    // --- Network (Wi-Fi) Status ---
    let networkListener: any = null;

    const setupNetwork = async () => {
      if (isNative) {
        const status = await Network.getStatus();
        if (isMounted) setState(prev => ({ ...prev, isOnline: status.connected }));

        networkListener = await Network.addListener('networkStatusChange', status => {
          if (isMounted) setState(prev => ({ ...prev, isOnline: status.connected }));
        });
      } else {
        const handleOnline = () => {
          if (isMounted) setState(prev => ({ ...prev, isOnline: true }));
        };
        const handleOffline = () => {
          if (isMounted) setState(prev => ({ ...prev, isOnline: false }));
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
        };
      }
    };

    const networkCleanupPromise = setupNetwork();

    // --- Battery Status ---
    let batteryManager: any = null;
    let batteryInterval: any = null;

    const updateBatteryInfo = async () => {
      if (!isMounted) return;

      if (isNative) {
        try {
          const info = await Device.getBatteryInfo();
          if (isMounted) {
            setState(prev => ({
              ...prev,
              batteryLevel: info.batteryLevel !== undefined ? Math.round(info.batteryLevel * 100) : null,
              isCharging: info.isCharging || false
            }));
          }
        } catch (err) {
          console.warn('Capacitor Battery Info error', err);
        }
      } else if (batteryManager) {
        setState(prev => ({
          ...prev,
          batteryLevel: Math.round(batteryManager.level * 100),
          isCharging: batteryManager.charging
        }));
      }
    };

    const setupBattery = async () => {
      if (isNative) {
        updateBatteryInfo();
        // Capacitor Device plugin doesn't have a listener for battery, so we poll
        batteryInterval = setInterval(updateBatteryInfo, 10000);
      } else if ('getBattery' in navigator) {
        try {
          const battery = await (navigator as any).getBattery();
          if (!isMounted) return;
          batteryManager = battery;
          updateBatteryInfo();

          batteryManager.addEventListener('levelchange', updateBatteryInfo);
          batteryManager.addEventListener('chargingchange', updateBatteryInfo);
        } catch (err) {
          console.warn('Web Battery Status API error', err);
        }
      }
    };

    setupBattery();

    return () => {
      isMounted = false;
      if (networkListener) networkListener.remove();
      if (batteryInterval) clearInterval(batteryInterval);
      if (batteryManager) {
        batteryManager.removeEventListener('levelchange', updateBatteryInfo);
        batteryManager.removeEventListener('chargingchange', updateBatteryInfo);
      }
      networkCleanupPromise.then(cleanup => {
        if (cleanup) cleanup();
      });
    };
  }, []);

  return state;
}
